import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { SessionStorageService } from '../web-storage';

@Injectable()
/** Service para gerenciamento dos states dos components da aplicação */
export class StateService<T> implements OnDestroy {
    /** Se deve usar o storage para armazenar o state localmente */
    private useStorage: boolean;
    /** Storage Key do session storage para armazenar localmente o state */
    protected STORAGE_KEY: string;

    /** State */
    protected state: T;
    /** State padrão */
    protected DEFAULT_STATE: T;
    /** Subject para inscrições de mudança do state */
    protected _state$ = new BehaviorSubject<T>(null);

    /** BehaviorSubject que indica se está carregando a view */
    private _isLoading$ = new BehaviorSubject<boolean>(false);

    /** Assinaturas de eventos */
    protected subscriptions = new Subscription();

    constructor(
        private sessionStorageService: SessionStorageService,
        DEFAULT_STATE: T
    ) {
        this.DEFAULT_STATE = DEFAULT_STATE;

        // Assinaturas
        this.subscriptions.add(
            // Assina o evento de limpeza do session storage
            this.sessionStorageService.clearAllSessionStorage$.subscribe(() => this.clear())
        );
    }

    /** Inicializa o state */
    init(useStorage: boolean, storageKey: string = null): Observable<T> {
        this.useStorage = useStorage;
        if (this.useStorage) {
            this.STORAGE_KEY = storageKey;
            this.getFromStorage();
        } else {
            this.state = this.DEFAULT_STATE;
        }

        return this.state$;
    }

    /** Obtem o state */
    get state$(): Observable<T> {
        return this._state$.asObservable();
    }

    /** Propaga as alterações do state */
    propagateState() {
        this._state$.next(this.state);
        this.setToStorage();
    }

    /** Reseta o state para o padrão */
    resetState(): void {
        this.state = this.DEFAULT_STATE;
        this.propagateState();
    }

    /** Flag que indica se está carregando a view */
    get isLoading$(): Observable<boolean> {
        return this._isLoading$.asObservable();
    }

    /** Seta se está carregando a view */
    setIsLoading(isLoading: boolean) {
        this._isLoading$.next(isLoading);
    }

    /** Carrega o state do sessionStorage ao iniciar o serviço */
    private getFromStorage(): void {
        try {
            if (!this.useStorage) {
                return;
            }

            const storedState = this.sessionStorageService.getObject(this.STORAGE_KEY) as T;
            this.state = storedState ?? this.DEFAULT_STATE;
            this._state$.next(this.state);
        } catch (error) {
            console.error(`Erro ao carregar o state do widget do sessionStorage (${this.STORAGE_KEY}):`, error);
        }
    }

    /** Salva o state atual no sessionStorage */
    private setToStorage(): void {
        try {
            if (!this.useStorage) {
                return;
            }

            this.sessionStorageService.setObject(this.STORAGE_KEY, this.state);
        } catch (error) {
            console.error(`Erro ao salvar o state do widget no sessionStorage (${this.STORAGE_KEY}):`, error);
        }
    }

    /** Remove o statu do sessionStorage */
    private removeFromStorage(): void {
        try {
            if (!this.useStorage) {
                return;
            }

            this.sessionStorageService.remove(this.STORAGE_KEY);
        } catch (error) {
            console.error(`Erro ao remover o state do widget no sessionStorage (${this.STORAGE_KEY}):`, error);
        }
    }

    /** Reseta o state e remove do sessionStorage */
    private clear() {
        this.resetState();
        this.removeFromStorage();
    }

    ngOnDestroy() {
        this._state$?.unsubscribe();
        this.subscriptions?.unsubscribe();
    }
}
